import { useLocation, useMatch, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useCasePartnerShell } from '@/components/contexts/CasePartnerContext';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { MatterJurisdictionProvider } from '@/components/contexts/MatterJurisdictionContext';
import { useNavigation } from '@/components/contexts/NavigationContext';
import { ShellHeaderProvider } from '@/components/contexts/ShellHeaderContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import useMatter from '@/hooks/useMatter';
import { CASE_PARTNER_HISTORY_WIDTH, getSidePanelAwareRightInset, PANEL_RIGHT_GAP } from '@/lib/nav-constants';
import { setToastHost } from '@/lib/toastHost';
import { cn } from '@/lib/utils';
import Drawer from './Drawer';
import Header from './Header';
import { ResizableSidePanel } from './ResizableSidePanel';
import { ShellHeader } from './ShellHeader';

export interface AppShellProps {
  children: ReactNode;
}

export default function AppShell({ children }: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { collapsed: navCollapsed } = useNavigation();
  const {
    openDocumentPanel,
    showCasePartnerTab,
    removeCasePartnerTab,
    isOpen: sidePanelOpen,
    panelWidth,
  } = useSidePanel();
  const casePartnerShell = useCasePartnerShell();
  const location = useLocation();
  const navigate = useNavigate();
  const previousPathRef = useRef(location.pathname);
  const restoreCasePartnerPanelOpenRef = useRef(false);
  const { selectedCase } = useCaseContext();
  const slug = useMatch({
    from: '/matter/$slug',
    shouldThrow: false,
    select: (match) => match.params.slug,
  });
  const matterGuid = slug ?? selectedCase;
  const { matter } = useMatter(matterGuid);
  const isOnCasePartnerPage = location.pathname.includes('/case-partner');
  const reservedContentWidth = casePartnerShell.isChatHistoryDocked ? CASE_PARTNER_HISTORY_WIDTH : 0;
  const scrollbarGutterClass = '[scrollbar-gutter:stable_both-edges]';

  // Global citation + deadline click handler - works across all pages
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const deadlineTarget = (e.target as HTMLElement).closest<HTMLElement>('.deadline-link');
      if (deadlineTarget) {
        if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          const deadlineId = deadlineTarget.getAttribute('data-deadline-id') || '';
          if (deadlineId && matterGuid) {
            showCasePartnerTab({ activate: true, open: true });
            void navigate({
              to: '/matter/$slug/deadlines',
              params: { slug: matterGuid },
              search: { taskId: deadlineId },
            }).finally(() => {
              window.setTimeout(() => {
                showCasePartnerTab({ activate: true, open: true });
              }, 0);
            });
          }
        }
        return;
      }

      const target = (e.target as HTMLElement).closest<HTMLElement>('.citation-link');
      if (!target || e.button !== 0) return;

      // Docket citations are real <a href> targets. Leave modifier/middle-clicks
      // to the browser (Open in New Tab). Intercept unmodified left-click for
      // in-app navigation. Always derive the route from slug + guid rather than
      // trusting an href from rendered HTML.
      const docketEntryGuid = target.getAttribute('data-docket-entry-guid');
      if (docketEntryGuid && matterGuid) {
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.preventDefault();
        e.stopPropagation();
        void navigate({
          to: '/matter/$slug/dockets',
          params: { slug: matterGuid },
          search: { entry: docketEntryGuid },
        });
        return;
      }

      // Public-web cites are real <a href target="_blank"> chips. Leave every
      // click to the browser, including modifier/middle-click.
      if (target.getAttribute('data-web-url') || target.classList.contains('citation-link-web')) {
        return;
      }

      if (e.ctrlKey || e.metaKey || e.shiftKey) return;

      const num = target.getAttribute('data-cite');
      const documentGuid = target.getAttribute('data-doc-guid') || '';
      const chunkIdStr = target.getAttribute('data-chunk-id');
      const chunkId = chunkIdStr ? parseInt(chunkIdStr, 10) : undefined;
      const segmentIndexStr = target.getAttribute('data-segment-index');
      const segmentIndex = segmentIndexStr ? parseInt(segmentIndexStr, 10) : undefined;
      const cellRange = target.getAttribute('data-cell-range') || undefined;
      if (documentGuid && matterGuid) {
        e.preventDefault();
        e.stopPropagation();
        openDocumentPanel({
          documentGuid,
          matterGuid,
          citationNumber: num || '',
          chunkId: chunkId,
          segmentIndex,
          cellRange,
          activeTab: 'document',
        });
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [matterGuid, openDocumentPanel, navigate, showCasePartnerTab]);

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;
    const wasOnCasePartner = previousPath.includes('/case-partner');
    const isOnCasePartner = currentPath.includes('/case-partner');
    const enteredCasePartner = !wasOnCasePartner && isOnCasePartner;
    const leftCasePartner = wasOnCasePartner && !isOnCasePartner;
    const isSameMatterRoute = Boolean(
      casePartnerShell.matterGuid &&
      currentPath.startsWith(`/matter/${casePartnerShell.matterGuid}/`)
    );

    if (enteredCasePartner) {
      restoreCasePartnerPanelOpenRef.current = sidePanelOpen;
    }

    if (isOnCasePartner) {
      removeCasePartnerTab();
    } else if (
      leftCasePartner &&
      isSameMatterRoute &&
      casePartnerShell.hasConversationActivity
    ) {
      if (sidePanelOpen || restoreCasePartnerPanelOpenRef.current) {
        showCasePartnerTab({ activate: false, open: true });
      } else {
        showCasePartnerTab({ activate: false });
      }
      restoreCasePartnerPanelOpenRef.current = false;
    }

    previousPathRef.current = currentPath;
  }, [
    location.pathname,
    casePartnerShell.matterGuid,
    casePartnerShell.hasConversationActivity,
    removeCasePartnerTab,
    showCasePartnerTab,
    sidePanelOpen,
  ]);

  return (
    <div className="h-screen flex flex-col bg-white">
      <Drawer sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Header setSidebarOpen={setSidebarOpen} sidebarOpen={sidebarOpen} />
      <main
        data-scroll-restoration-id="main-scrollable-area"
        className={cn(
          "flex-1 flex flex-col overflow-hidden min-h-0 bg-primary-50 transition-[padding] duration-300 ease-in-out",
          navCollapsed ? "lg:pl-20" : "lg:pl-56",
        )}
        style={{
          // Cap at the overlay threshold so the content area never reflows when the
          // panel switches modes — it stays pinned at MIN_CONTENT_WIDTH throughout.
          paddingRight: sidePanelOpen
            ? `${getSidePanelAwareRightInset(panelWidth, navCollapsed, reservedContentWidth) + PANEL_RIGHT_GAP}px`
            : '',
          transition: 'none' // Disable transition for instant response during drag
        }}
      >
        {/* Absolute children of <main> use its padding edge, which still spans the
            full viewport under the sidebar. Host toasts in this content-box
            wrapper so they center in the visible main column. */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div ref={setToastHost} className="pointer-events-none absolute inset-0 z-[100000]" />
          <MatterJurisdictionProvider region={matter?.region} forum={matter?.forum ?? null}>
            <ShellHeaderProvider>
              {/* Header sits outside the scroll region so its bottom border always spans
                  the full content width, even when page content scrolls horizontally. */}
              <ShellHeader scrollbarGutterClass={scrollbarGutterClass} />
              <div
                className={cn(
                  'min-h-0 flex-1',
                  isOnCasePartnerPage
                    ? 'overflow-hidden'
                    : cn('overflow-auto', scrollbarGutterClass),
                )}
              >
                {children}
              </div>
            </ShellHeaderProvider>

            {/* Resizable side panel with drag handle - fixed positioned */}
            <ResizableSidePanel />
          </MatterJurisdictionProvider>
        </div>
      </main>
      </div>
  );
}
